import React, { useState, useEffect } from 'react';
import ModalShell from '@/components/ModalShell';
import SectionIcon from '@/components/SectionIcon';
import { I } from '@/lib/icons';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useToast } from '@/components/Toast';
import { isValidIban, isValidIco } from '@/lib/validators';

const TYPE_OPTS = [
  { id: 'szco', label: 'SZČO', desc: 'Živnostník', icon: 'users' },
  { id: 'sro',  label: 's.r.o.', desc: 'Spoločnosť', icon: 'bank' },
];

const EMPTY = {
  type: 'szco',
  name: '',
  ico: '',
  dic: '',
  ic_dph: '',
  is_dph_payer: false,
  iban: '',
  bank: '',
  address: '',
};

export default function AddWorkspaceModal({ open, onClose }) {
  const { create } = useWorkspace();
  const toast = useToast();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(EMPTY);
  }, [open]);

  if (!open) return null;

  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const submit = async () => {
    const name = form.name.trim();
    const ico = form.ico.trim();
    const iban = form.iban.replace(/\s+/g, '');
    if (!name) { toast.error('Zadaj názov firmy.'); return; }
    if (!isValidIco(ico)) { toast.error('IČO musí mať 6 až 8 cifier.'); return; }
    if (!isValidIban(iban)) { toast.error('Zadaj platný slovenský IBAN (SK + 22 cifier).'); return; }
    setSaving(true);
    try {
      const ws = await create({
        name,
        type: form.type,
        ico,
        dic: form.dic.trim() || null,
        ic_dph: form.is_dph_payer ? (form.ic_dph.trim() || null) : null,
        is_dph_payer: !!form.is_dph_payer,
        default_dph_rate: form.is_dph_payer ? 23 : 0,
        address: form.address.trim() || null,
        iban,
        bank: form.bank.trim() || null,
      });
      toast.success(`Firma ${ws.name} vytvorená.`);
      onClose();
    } catch (e) {
      toast.error(e.message || 'Nepodarilo sa vytvoriť firmu.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={saving ? undefined : onClose}
      title="Pridať novú firmu"
      maxWidth={560}
      rightAction={
        <button
          onClick={submit}
          disabled={saving}
          data-testid="save-workspace"
          className="cd-btn cd-btn-primary cd-btn-sm"
        >
          {saving
            ? <><span className="cd-spinner" style={{ borderTopColor: '#fff' }} /> Vytváram...</>
            : <><I.check /> Vytvoriť</>}
        </button>
      }
    >
      <div style={{ padding: '18px 22px 20px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* Typ firmy */}
        <div>
          <FieldLabel>Typ firmy</FieldLabel>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {TYPE_OPTS.map(o => {
              const active = form.type === o.id;
              return (
                <button
                  key={o.id}
                  type="button"
                  data-testid={`add-ws-type-${o.id}`}
                  onClick={() => set({ type: o.id, is_dph_payer: o.id === 'sro' })}
                  style={{
                    padding: '12px 14px',
                    background: active ? 'var(--product-primary-soft)' : 'var(--bg-surface)',
                    border: `2px solid ${active ? 'var(--product-primary)' : 'var(--border)'}`,
                    borderRadius: 'var(--r-md)',
                    cursor: 'pointer', textAlign: 'left',
                    display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'inherit',
                  }}
                >
                  <SectionIcon color="green" size={28}>
                    {React.createElement(I[o.icon], { width: 14, height: 14 })}
                  </SectionIcon>
                  <div>
                    <div style={{
                      fontSize: 14, fontWeight: 700, fontFamily: 'var(--font-display)',
                      color: active ? 'var(--product-primary-hover)' : 'var(--text-primary)',
                    }}>{o.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{o.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Základné údaje */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Názov firmy" full
            value={form.name}
            onChange={v => set({ name: v })}
            placeholder={form.type === 'sro' ? 'Moja firma s.r.o.' : 'Meno Priezvisko'}
            testId="add-ws-name"
          />
          <Field label="IČO" mono
            value={form.ico}
            onChange={v => set({ ico: v.replace(/[^0-9]/g, '') })}
            placeholder="12345678"
            testId="add-ws-ico"
          />
          <Field label="DIČ" mono
            value={form.dic}
            onChange={v => set({ dic: v })}
            placeholder="1080123456"
          />
          <Field label="Adresa" full
            value={form.address}
            onChange={v => set({ address: v })}
            placeholder="Hlavná 1, 811 01 Bratislava"
          />
        </div>

        {/* DPH toggle */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 12px',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
        }}>
          <input
            type="checkbox"
            id="add-ws-is-dph"
            checked={form.is_dph_payer}
            onChange={e => set({ is_dph_payer: e.target.checked })}
            style={{ width: 18, height: 18, cursor: 'pointer' }}
          />
          <label htmlFor="add-ws-is-dph" style={{ fontSize: 13, cursor: 'pointer' }}>
            Som platca DPH (mám IČ DPH)
          </label>
        </div>
        {form.is_dph_payer && (
          <Field label="IČ DPH" mono full
            value={form.ic_dph}
            onChange={v => set({ ic_dph: v })}
            placeholder="SK1080123456"
          />
        )}

        {/* Banka */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
          <Field label="IBAN" mono full
            value={form.iban}
            onChange={v => set({ iban: v })}
            placeholder="SK89 1100 0000 0029 4501 8765"
            testId="add-ws-iban"
          />
          <Field label="Banka (voliteľné)" full
            value={form.bank}
            onChange={v => set({ bank: v })}
            placeholder="Tatra banka, a.s."
          />
        </div>
      </div>
    </ModalShell>
  );
}

function FieldLabel({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
      textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6,
    }}>{children}</div>
  );
}

function Field({ label, value, onChange, mono, full, placeholder, testId }) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : 'auto' }}>
      <FieldLabel>{label}</FieldLabel>
      <input
        className={`cd-input${mono ? ' mono' : ''}`}
        data-testid={testId}
        value={value || ''}
        onChange={e => onChange?.(e.target.value)}
        placeholder={placeholder}
        style={{ padding: '10px 12px', fontSize: 14 }}
      />
    </div>
  );
}
